import React, { useState } from 'react';
import { 
  multiFactor,
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator
} from 'firebase/auth';
import { useLanguage } from '../../contexts/LanguageContext';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

interface MFAVerificationProps {
  resolver: MultiFactorResolver;
  onSuccess: () => void;
  onCancel: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({ resolver, onSuccess, onCancel }) => {
  const { t } = useLanguage();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    try {
      setLoading(true);
      setError('');

      if (verificationCode.length !== 6) {
        setError(t('mfa.verification.messages.invalidCode'));
        return;
      }

      const selectedHint = resolver.hints[0];
      
      let multiFactorAssertion;
      
      if (selectedHint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
          selectedHint.uid,
          verificationCode
        );
      } else if (selectedHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        setError('SMS-alapú MFA nem támogatott ebben a verzióban');
        return;
      } else {
        setError('Ismeretlen MFA típus');
        return;
      }

      await resolver.resolveSignIn(multiFactorAssertion);
      onSuccess();
    } catch (err: any) {
      if (err.code === 'auth/invalid-verification-code') {
        setError(t('mfa.verification.messages.invalidCode'));
      } else if (err.code === 'auth/code-expired') {
        setError(t('mfa.verification.messages.expiredCode'));
      } else if (err.code === 'auth/quota-exceeded') {
        setError(t('mfa.verification.messages.quotaExceeded'));
      } else if (err.code === 'auth/too-many-requests') {
        setError(t('mfa.verification.messages.tooManyAttempts'));
      } else {
        // Csak ismeretlen hibáknál logolunk
        setError(t('mfa.verification.messages.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in duration-200">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-primary-500/30">
            <ShieldCheckIcon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('mfa.verification.title')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('mfa.verification.description')}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl animate-in slide-in-from-top duration-200">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('mfa.verification.codeLabel')}
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyPress={handleKeyPress}
              placeholder={t('mfa.verification.codePlaceholder')}
              maxLength={6}
              autoFocus
              className="w-full px-4 py-4 text-center text-3xl font-mono border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all tracking-widest"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              {t('mfa.verification.codeRefreshes')}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleVerify}
              disabled={loading || verificationCode.length !== 6}
              className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-4 py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-500/30"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('mfa.verification.buttons.verifying')}
                </div>
              ) : (
                t('mfa.verification.buttons.verify')
              )}
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium"
            >
              {t('mfa.verification.buttons.cancel')}
            </button>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>{t('mfa.verification.tip.title')}</strong> {t('mfa.verification.tip.description')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
